import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { supplierInquiriesTabPath } from "@/features/settings/paths"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Lock } from "lucide-react"
import { SectionLabel } from "../../../shared/SectionLabel"
import type { AttachmentFormat, Lang } from "./inquiryDraftMock"

const FORMAT_OPTIONS: ReadonlyArray<
  readonly [AttachmentFormat, TranslationKeys]
> = [
  ["pdf", "MC_INQ_DRAFT_FORMAT_PDF"],
  ["excel", "MC_INQ_DRAFT_FORMAT_EXCEL"],
  ["pdf_and_excel", "MC_INQ_DRAFT_FORMAT_PDF_EXCEL"],
] as const

const LANGUAGE_OPTIONS: ReadonlyArray<readonly [Lang, TranslationKeys]> = [
  ["de", "MC_INQ_DRAFT_LANG_DE_FULL"],
  ["en", "MC_INQ_DRAFT_LANG_EN_FULL"],
] as const

interface AttachmentLanguageSectionProps {
  format: AttachmentFormat
  setFormat: (next: AttachmentFormat) => void
  // The org's settings can ENFORCE one format (backend applies it on
  // send/reminder/preview/download too). The select then shows the enforced value
  // read-only rather than offering a choice the send would silently ignore.
  formatLocked: boolean
  language: Lang
  setLanguage: (next: Lang) => void
}

/**
 * Where the user decides WHAT goes out: which file(s) are attached and in which
 * language. Both are single-valued and batch-wide — an inquiry sends one format in
 * one language — so they sit up front, before the copy is written. The header's
 * PDF|Excel tabs are a pure preview affordance downstream of the format chosen here.
 */
export function AttachmentLanguageSection({
  format,
  setFormat,
  formatLocked,
  language,
  setLanguage,
}: AttachmentLanguageSectionProps) {
  const { translate } = useTranslation()

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{translate("MC_INQ_DRAFT_SEC_ATTACHMENT")}</SectionLabel>
      {/* Two fields side by side; each wraps to its own row once the container
          gets too narrow to fit both (e.g. the composer panel resized narrow). */}
      <div className="flex flex-wrap gap-3">
        <div className="flex min-w-[160px] flex-1 flex-col gap-1.5">
          <span className="flex items-center gap-1 text-[11.5px] text-grey5">
            {translate("MC_INQ_DRAFT_ATTACHMENT_FORMAT")}
          </span>
          {format === "none" ? (
            // "none" (a declaration-review Rückfrage, or its reminder) is never a
            // choice the composer offers: the backend sets/keeps it, an org-level
            // lock can't override it (see InquiryDraftPage's effectiveFormat), so
            // there is nothing to pick here, only to state.
            <div className="flex h-9 w-full items-center rounded-md border border-grey4 bg-grey2 px-3 text-[13px] text-grey5">
              {translate("MC_INQ_DRAFT_FORMAT_NONE")}
            </div>
          ) : formatLocked ? (
            // Locked in Settings: a plain field, not a dropdown that ignores clicks, and
            // the way to change it. A new tab, so the draft's edits stay open here.
            <>
              <div className="flex h-9 w-full items-center gap-2 rounded-md border border-grey4 bg-grey2 px-3 text-[13px] text-grey5">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                {translate(
                  FORMAT_OPTIONS.find(([value]) => value === format)?.[1] ??
                    "MC_INQ_DRAFT_FORMAT_PDF",
                )}
              </div>
              <span className="text-[11.5px] text-grey5">
                {translate("MC_INQ_DRAFT_FORMAT_LOCKED")}{" "}
                <a
                  href={supplierInquiriesTabPath("attachment")}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white underline hover:text-yellow1"
                >
                  {translate("MC_INQ_DRAFT_FORMAT_LOCKED_LINK")}
                </a>
              </span>
            </>
          ) : (
            <Select
              value={format}
              onValueChange={(v) => setFormat(v as AttachmentFormat)}
            >
              <SelectTrigger
                aria-label={translate("MC_INQ_DRAFT_ATTACHMENT_FORMAT")}
                className="h-9 w-full border-grey4 bg-grey2 text-[13px] text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map(([value, key]) => (
                  <SelectItem key={value} value={value} className="text-[13px]">
                    {translate(key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex min-w-[160px] flex-1 flex-col gap-1.5">
          <span className="text-[11.5px] text-grey5">
            {translate("MC_INQ_DRAFT_ATTACHMENT_LANGUAGE")}
          </span>
          <Select
            value={language}
            onValueChange={(v) => setLanguage(v as Lang)}
          >
            <SelectTrigger
              aria-label={translate("MC_INQ_DRAFT_ATTACHMENT_LANGUAGE")}
              className="h-9 w-full border-grey4 bg-grey2 text-[13px] text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map(([value, key]) => (
                <SelectItem key={value} value={value} className="text-[13px]">
                  {translate(key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
